import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, Field, Input, Text, VStack, Center, Image } from '@chakra-ui/react';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import { useAuth } from '@/auth/AuthContext';
import useResendCooldown from '@/auth/useResendCooldown';
import AccountService from '@/services/AccountService';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login, completeTwoFactorLogin } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState('credentials');
    const [pendingToken, setPendingToken] = useState('');
    const [twoFactorMethod, setTwoFactorMethod] = useState('Totp');
    const [code, setCode] = useState('');
    const [twoFactorError, setTwoFactorError] = useState('');
    const [twoFactorInfo, setTwoFactorInfo] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const resendCooldown = useResendCooldown();
    const isEmailMethod = twoFactorMethod === 'Email';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await login(email, password);
            if (result?.success) {
                navigate('/');
            } else if (result?.requiresTwoFactor) {
                setPendingToken(result.pendingToken);
                setTwoFactorMethod(result.twoFactorMethod);
                if (result.twoFactorMethod === 'Email') resendCooldown.start();
                setStep('twoFactor');
            } else {
                setError(result?.error || 'Invalid email or password');
            }
        } catch (err) {
            setError(err?.response?.status === 401 ? 'Invalid email or password' : 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsVerifying(true);
        try {
            const result = await completeTwoFactorLogin(pendingToken, code);
            if (result?.success) {
                navigate('/');
            } else {
                setTwoFactorError('The code is invalid or has expired.');
            }
        } catch {
            setTwoFactorError('The code is invalid or has expired.');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsResending(true);
        try {
            await AccountService.resendTwoFactorLoginCode(pendingToken);
            setTwoFactorInfo('We sent a new code.');
            resendCooldown.start();
        } catch (err) {
            if (err?.response?.status === 429) resendCooldown.start();
            else setTwoFactorError('Your sign-in session has expired. Please sign in again.');
        } finally {
            setIsResending(false);
        }
    };

    const handleBackToCredentials = () => {
        setStep('credentials');
        setCode('');
        setTwoFactorError('');
        setTwoFactorInfo('');
        setPendingToken('');
        setTwoFactorMethod('Totp');
        resendCooldown.reset();
    };

    if (step === 'twoFactor') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary">
                <Box w="400px" p={8} bg="bg.secondary" borderRadius={16}>
                    <VStack gap={6} as="form" onSubmit={handleVerifyCode}>
                        <Image src={Logo} w="70%" mb={2} />
                        <Text fontSize={24} color="white" fontWeight="bold">Confirm sign-in</Text>
                        <Text color="text.grey" fontSize={14} textAlign="center">
                            {isEmailMethod
                                ? 'We sent a 6-digit code to your e-mail address. It is valid for 10 minutes.'
                                : 'Enter the code from your Authenticator app.'}
                        </Text>

                        <Field.Root required w="full">
                            <Field.Label color="white">Code <Field.RequiredIndicator /></Field.Label>
                            <Input
                                variant="primary"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="123456"
                                value={code}
                                onChange={e => setCode(e.target.value)}
                            />
                        </Field.Root>

                        {twoFactorError && (
                            <Text color="red.400" fontSize={14} w="full">{twoFactorError}</Text>
                        )}
                        {twoFactorInfo && !twoFactorError && (
                            <Text color="green.400" fontSize={14} w="full">{twoFactorInfo}</Text>
                        )}

                        <Button variant="primary" type="submit" w="full" loading={isVerifying}>
                            Confirm
                        </Button>

                        {isEmailMethod && (
                            <Button
                                variant="outlined"
                                w="full"
                                type="button"
                                onClick={handleResendCode}
                                loading={isResending}
                                disabled={resendCooldown.isCoolingDown}
                            >
                                {resendCooldown.isCoolingDown ? `Resend in ${resendCooldown.secondsLeft}s` : 'Resend code'}
                            </Button>
                        )}

                        <Text
                            color="text.grey"
                            fontSize={14}
                            cursor="pointer"
                            _hover={{ color: 'white' }}
                            onClick={handleBackToCredentials}
                        >
                            Back to sign in
                        </Text>
                        <Text color="text.grey" fontSize={12} textAlign="center">
                            Lost access to your second factor? Ask another administrator to reset your 2FA.
                        </Text>
                    </VStack>
                </Box>
            </Center>
        );
    }

    return (
        <Center minH="100vh" w="100vw" bg="bg.primary">
            <Box w="400px" p={8} bg="bg.secondary" borderRadius={16}>
                <VStack gap={6} as="form" onSubmit={handleSubmit}>
                    <Image src={Logo} w="70%" mb={2} />
                    <Text fontSize={24} color="white" fontWeight="bold">Sign in</Text>

                    <Field.Root required w="full">
                        <Field.Label color="white">Email <Field.RequiredIndicator /></Field.Label>
                        <Input
                            variant="primary"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </Field.Root>

                    <Field.Root required w="full">
                        <Field.Label color="white">Password <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                    </Field.Root>

                    {error && (
                        <Text color="red.400" fontSize={14} w="full">{error}</Text>
                    )}

                    <Button variant="primary" type="submit" w="full" loading={isLoading}>
                        Sign in
                    </Button>

                    <Link to="/forgot-password">
                        <Text color="text.grey" fontSize={14} _hover={{ color: 'white' }}>
                            Forgot password
                        </Text>
                    </Link>
                </VStack>
            </Box>
        </Center>
    );
};

export default LoginPage;
